
import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../toaster';
import {
    IBooking,
    CreateWellnessPackageBookingRequest,
    CreateSpecialistBookingRequest,
    BookingQueryParams,
    BookingState,
    UpdateBookingRequest
} from '../../types/bookings';
import { downloadCsvFile } from '../../utils/export';

const BASE_URL = '/bookings';

const fetchBookingPage = async (filters: BookingQueryParams, page: number, limit: number) => {
    const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
    );
    const queryParams = new URLSearchParams(cleanFilters as any);
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));
    return doGET(`${BASE_URL}?${queryParams.toString()}`);
};

const formatBookingDate = (value?: string) => {
    if (!value) return '';
    if (value.length === 8) {
        return `${value.substring(6, 8)}/${value.substring(4, 6)}/${value.substring(0, 4)}`;
    }
    return value;
};

const toLabelCase = (value?: string) => {
    if (!value) return '';
    return value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const deriveCorporateNameFromEmail = (email?: string) => {
    if (!email || !email.includes('@')) return '';

    const domain = email.split('@')[1] || '';
    const companySegment = domain.split('.')[0] || '';

    return companySegment
        ? companySegment.charAt(0).toUpperCase() + companySegment.slice(1)
        : '';
};

const buildCorporateNameMap = async () => {
    const response = await doGET('/corporates');
    const corporates = Array.isArray(response?.data?.data) ? response.data.data : [];

    return new Map<string, string>(
        corporates.map((corporate: any) => [
            corporate?._id || '',
            corporate?.companyName || corporate?.name || '',
        ])
    );
};

const store = create<BookingState>((set, get) => ({
    data: [],
    totalPages: 0,
    total: 0,
    currentPage: 1,
    filters: {},
    isLoading: false,
    limit: 10,

    // GET /bookings
    fetchGrid: async () => {
        try {
            const { filters, currentPage, limit, isLoading } = get();
            if (isLoading) return;

            set({ isLoading: true });

            const response = await fetchBookingPage(filters, currentPage, limit);

            if (response.status >= 200 && response.status < 400) {
                const resData = response.data?.data || response.data;
                const rows = resData?.rows || resData;
                const total = resData?.total ?? (Array.isArray(rows) ? rows.length : 0);
                set({
                    data: Array.isArray(rows) ? rows : [],
                    ...(currentPage === 1 && {
                        totalPages: Math.ceil(total / limit),
                        total: total
                    }),
                });
            } else {
                showError(response.message);
            }
        } catch (err) {
            showError('Failed to fetch bookings');
        } finally {
            set({ isLoading: false });
        }
    },

    setFilters: (newFilters: BookingQueryParams) => {
        set({ filters: newFilters, currentPage: 1 });
        get().fetchGrid();
    },

    nextPage: () => {
        const { currentPage, totalPages, fetchGrid } = get();
        if (currentPage < totalPages) {
            set(state => ({
                currentPage: state.currentPage + 1
            }));
            fetchGrid();
        }
    },

    prevPage: () => {
        const { currentPage, fetchGrid } = get();
        if (currentPage > 1) {
            set(state => ({
                currentPage: state.currentPage - 1
            }));
            fetchGrid();
        }
    },

    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        const { totalPages } = get();
        const newPage = page + 1;
        if (newPage >= 1 && newPage <= totalPages) {
            set({
                currentPage: newPage
            });
            get().fetchGrid();
        }
    },

    // POST /bookings/specialist - Create Specialist Booking
    onCreateSpecialist: async (data: CreateSpecialistBookingRequest) => {
        try {
            const response = await doPOST(`${BASE_URL}/specialist`, data);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                return response.data?.data || response.data || null;
            } else {
                showError(response.message || 'Failed to create specialist booking');
                return null;
            }
        } catch (error) {
            showError('Failed to create specialist booking');
            return null;
        }
    },

    // POST /bookings/wellness-package - Create Wellness Package Booking
    onCreateWellnessPackage: async (data: CreateWellnessPackageBookingRequest) => {
        try {
            const response = await doPOST(`${BASE_URL}/wellness-package`, data);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                return response.data?.data || response.data || null;
            } else {
                showError(response.message || 'Failed to create wellness package booking');
                return null;
            }
        } catch (error) {
            showError('Failed to create wellness package booking');
            return null;
        }
    },

    // PUT /bookings/:id - Update Booking
    onUpdate: async (id: string, data: UpdateBookingRequest) => {
        try {
            const response = await doPUT(`${BASE_URL}/${id}`, data);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
                return response.data?.data || response.data || null;
            } else {
                showError(response.message || 'Failed to update booking');
                return null;
            }
        } catch (error) {
            showError('Failed to update booking');
            return null;
        }
    },

    // DELETE /bookings/:id
    onDelete: async (id: string) => {
        try {
            const response = await doDELETE(`${BASE_URL}/${id}`);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            } else {
                showError(response.message || 'Failed to delete booking');
            }
        } catch (error) {
            showError('Failed to delete booking');
        }
    },

    // GET /bookings/:id - Get Booking by ID
    detail: async (id: string) => {
        try {
            const response = await doGET(`${BASE_URL}/${id}`);
            if (response.status >= 200 && response.status < 400) {
                const bookingData = response.data?.data || response.data;
                return { data: bookingData };
            } else {
                showError(response.message || 'Failed to fetch booking details');
                return null;
            }
        } catch (error) {
            showError('Failed to fetch booking details');
            return null;
        }
    },

    // GET /bookings/user/:userId - Get Bookings by User ID
    fetchByUserId: async (userId: string) => {
        try {
            const response = await doGET(`${BASE_URL}/user/${userId}`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data || response.data;
            } else {
                showError(response.message || 'Failed to fetch bookings by user');
                return [];
            }
        } catch (error) {
            showError('Failed to fetch bookings by user');
            return [];
        }
    },

    // GET /bookings/upcoming - Get Upcoming Bookings
    fetchUpcoming: async () => {
        try {
            const response = await doGET(`${BASE_URL}/upcoming`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data || response.data;
            } else {
                showError(response.message || 'Failed to fetch upcoming bookings');
                return [];
            }
        } catch (error) {
            showError('Failed to fetch upcoming bookings');
            return [];
        }
    },

    exportBookings: async () => {
        try {
            const { filters } = get();
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, v]) => v !== undefined && v !== '')
            );
            const queryParams = new URLSearchParams(cleanFilters as any);
            const response = await doGET(`${BASE_URL}?${queryParams.toString()}`);
            if (!(response.status >= 200 && response.status < 400)) {
                showError(response.message || 'Failed to export bookings');
                return false;
            }

            const resData = response.data?.data || response.data;
            const bookings: IBooking[] = Array.isArray(resData?.rows)
                ? resData.rows
                : Array.isArray(resData)
                    ? resData
                    : [];

            if (!bookings.length) {
                showError('No bookings found to export');
                return false;
            }

            const corporateNameMap = await buildCorporateNameMap();
            const uniqueUserIds = Array.from(new Set(bookings.map((booking) => booking.userId).filter(Boolean)));
            const userEmailMap = new Map<string, string>();

            await Promise.all(
                uniqueUserIds.map(async (userId) => {
                    try {
                        const userResponse = await doGET(`/users/${userId}`);
                        const userData = userResponse?.data?.data?.user || userResponse?.data?.data || userResponse?.data || {};
                        userEmailMap.set(userId, userData?.email || '');
                    } catch (error) {
                        userEmailMap.set(userId, '');
                    }
                })
            );

            downloadCsvFile(
                `bookings-export-${new Date().toISOString().slice(0, 10)}.csv`,
                [
                    'Booking Date',
                    'Booking Time',
                    'Booking Type',
                    'Service Mode',
                    'Status',
                    'Payment Status',
                    'Price',
                    'Address',
                    'Pincode',
                    'Coupon Used',
                    'User ID',
                    'Corporate ID',
                    'Corporate Name',
                ],
                bookings.map((booking) => [
                    formatBookingDate(booking.bookingDate),
                    booking.bookingTime || '',
                    toLabelCase(booking.bookingType),
                    toLabelCase(booking.serviceMode),
                    toLabelCase(booking.status),
                    toLabelCase(booking.paymentStatus),
                    booking.price ?? '',
                    booking.serviceAddress?.addressLine || '',
                    booking.serviceAddress?.pincode || '',
                    booking.couponCode || '',
                    booking.userId || '',
                    booking.corporateId || '',
                    corporateNameMap.get(booking.corporateId || '') ||
                        deriveCorporateNameFromEmail(userEmailMap.get(booking.userId || '')) ||
                        '',
                ])
            );

            showSuccess('Bookings exported successfully');
            return true;
        } catch (error) {
            showError('Failed to export bookings');
            return false;
        }
    },
}));

export const useBookingStore = () => store((state) => state);
